import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Clock, Save, Loader2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// `name` is persisted to the backend as `timezone_name` (never translated).
const TIMEZONE_OPTIONS = [
  { id: 'America/Cayenne', name: 'Guyane Française (Cayenne)', offset: -3 },
  { id: 'America/Martinique', name: 'Martinique / Guadeloupe', offset: -4 },
  { id: 'America/Guadeloupe', name: 'Guadeloupe', offset: -4 },
  { id: 'Europe/Paris', name: 'France Métropolitaine (Paris)', offset: 1 },
  { id: 'Indian/Reunion', name: 'La Réunion', offset: 4 },
  { id: 'Pacific/Noumea', name: 'Nouvelle-Calédonie', offset: 11 },
  { id: 'Pacific/Tahiti', name: 'Polynésie Française (Tahiti)', offset: -10 },
];

// Timezone id -> i18n display subkey (display only; backend still receives raw `name`).
const TZ_NAME_KEYS = {
  'America/Cayenne': 'tzCayenne',
  'America/Martinique': 'tzMartinique',
  'America/Guadeloupe': 'tzGuadeloupe',
  'Europe/Paris': 'tzParis',
  'Indian/Reunion': 'tzReunion',
  'Pacific/Noumea': 'tzNoumea',
  'Pacific/Tahiti': 'tzTahiti',
};

export default function DocumentTimezonePage() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const tzName = (tz) => (tz && TZ_NAME_KEYS[tz.id]) ? t(`docTimezone.${TZ_NAME_KEYS[tz.id]}`) : (tz?.name || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTz, setSelectedTz] = useState('America/Cayenne');

  useEffect(() => {
    fetchTimezone();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTimezone = async () => {
    try {
      const res = await fetch(`${API}/api/company-settings/document-timezone`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTz(data.timezone_id || 'America/Cayenne');
      }
    } catch {
      // defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const tz = TIMEZONE_OPTIONS.find(t => t.id === selectedTz);
    if (!tz) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/company-settings/document-timezone`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timezone_id: tz.id,
          timezone_name: tz.name,
          utc_offset: tz.offset,
        }),
      });
      if (res.ok) {
        toast.success(t('docTimezone.toastUpdated'));
      } else {
        toast.error(t('docTimezone.errUpdate'));
      }
    } catch {
      toast.error(t('docTimezone.errNetwork'));
    } finally {
      setSaving(false);
    }
  };

  const currentTz = TIMEZONE_OPTIONS.find(t => t.id === selectedTz);
  const offsetStr = currentTz ? `UTC${currentTz.offset >= 0 ? '+' : ''}${currentTz.offset}` : 'UTC-3';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="document-timezone-page">
        <div className="flex items-center gap-3 mb-2">
          <Clock className="w-7 h-7 text-emerald-400" />
          <h1 className="text-2xl font-bold text-white">{t('docTimezone.title')}</h1>
        </div>
        <p className="text-gray-400 text-sm">
          {t('docTimezone.intro')}
        </p>

        <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
            <Globe className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-bold text-white">{t('docTimezone.sectionTimezone')}</h2>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">{t('docTimezone.selectLabel')}</label>
              <select
                data-testid="timezone-select"
                value={selectedTz}
                onChange={e => setSelectedTz(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-white text-sm focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 outline-none"
              >
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.id} value={tz.id}>
                    {tzName(tz)} (UTC{tz.offset >= 0 ? '+' : ''}{tz.offset})
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-lg bg-black/30 border border-white/5 p-4">
              <p className="text-sm text-gray-300">
                <span className="text-emerald-400 font-semibold">{t('docTimezone.currentLabel')}</span> {tzName(currentTz) || t('docTimezone.tzCayenne')} ({offsetStr})
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {t('docTimezone.selectHint')}
              </p>
            </div>

            <button
              data-testid="save-timezone-btn"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('docTimezone.save')}
            </button>
          </div>
        </div>

        <div className="rounded-lg bg-amber-900/20 border border-amber-600/30 p-4">
          <p className="text-sm text-amber-300 font-medium">{t('docTimezone.importantTitle')}</p>
          <p className="text-xs text-amber-200/70 mt-1">
            {t('docTimezone.importantBody')}
          </p>
        </div>
      </div>
    </Layout>
  );
}
